import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import {
	ACTION_NAME_LABEL_KEY,
	ACTION_NAME_TONE,
	actorDisplay,
	BULK_UPDATE_ACTION_NAME,
	CHANNEL_LABEL_KEY,
	composeWhatSublabel,
	composeWhatText,
	formatActivityLogWhen,
} from "../../pages/ActivityLog/GroupedActivityLog/activityLogGroupedDisplay";
import type { GroupedActivityLogEntry } from "../../pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { AccentIconTile } from "../UIToolkit/molecules/AccentIconTile";
import { EventListItem } from "../UIToolkit/molecules/EventListItem";
import { ShiftFieldChanges } from "./ShiftFieldChanges";
import { actionIcon } from "./shiftHistoryDisplay";

type Props = {
	entry: GroupedActivityLogEntry;
	isLatest: boolean;
	dataTestId: string;
};

const joined = (parts: (string | null)[]): string | undefined => parts.filter(Boolean).join(" · ") || undefined;

/** One Activity Log transaction that wrote the shift: what happened, the shift as it was left, who and when. */
export const ShiftHistoryEntry: FC<Props> = ({ entry, isLatest, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const timezone = chosenDepartment.timezone;
	const when = formatActivityLogWhen(entry.changedAt, timezone);
	const actionName = entry.kind === "resolved" ? entry.actionName : BULK_UPDATE_ACTION_NAME;
	const Icon = actionIcon(actionName);
	const leading = (
		<AccentIconTile size="xs" tone={ACTION_NAME_TONE[actionName]}>
			<Icon />
		</AccentIconTile>
	);

	if (entry.kind === "deferred") {
		return (
			<EventListItem
				leading={leading}
				title={t(ACTION_NAME_LABEL_KEY[actionName])}
				text={t("activityLog.grouped.deferred.label", { count: entry.groupSize })}
				meta={when}
				emphasis={isLatest}
				dataTestId={dataTestId}
			/>
		);
	}

	const actor = actorDisplay({ firstName: entry.performedByFirstName, username: entry.performedBy }).label;
	const channel = entry.channel === null ? null : t(CHANNEL_LABEL_KEY[entry.channel]);
	const reason = entry.details?.kind === "Shift" ? entry.details.reason : null;
	const summary = joined([composeWhatSublabel(entry.details, timezone, t), reason]);
	const changes = entry.shiftChanges !== null && entry.shiftChanges.length > 0 ? entry.shiftChanges : null;
	return (
		<EventListItem
			leading={leading}
			title={composeWhatText(entry.what, entry.details, t)}
			text={
				changes === null ? (
					summary
				) : (
					<>
						{summary}
						<ShiftFieldChanges changes={changes} dataTestId={`${dataTestId}-changes`} />
					</>
				)
			}
			meta={joined([when, actor, channel])}
			emphasis={isLatest}
			dataTestId={dataTestId}
		/>
	);
};
